// /////////////////////////////////////////////////////////////
// Fichier pour la gestion de la page detail du lot (plan)
// /////////////////////////////////////////////////////////////
import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  domain,
  languageContext,
  lotContext,
  noHeaderContext,
  pageContext,
  programmeContext,
  programmeLotsContext,
  programmeNameContext,
} from "../../contexts";
import Annotation from "../../components/annotation/annotation";
import Palette from "./../../components/palette/palette";
import Player from "./../../components/player/player";
import SvgIcon from "./../../components/svg/svg";

const DetailLot = ({ loadLot, loadHdForFrame }) => {
  // Hooks init
  const { lotParam } = useParams();
  const { lotDatas, setLotDatas } = useContext(lotContext);
  const lotsInfos = useContext(programmeLotsContext);
  const programmeName = useContext(programmeNameContext);
  const noHeader = useContext(noHeaderContext);
  const lang = useContext(languageContext);
  const programmeDatas = useContext(programmeContext);
  const { setCurrent, setHeaderLevel } = useContext(pageContext);

  // States
  const [sup, setSup] = useState("");
  const [currentDisplay, setCurrentDisplay] = useState("3D");
  const [displayPrices, setDisplayPrices] = useState(false);
  const [source2D, setSource2D] = useState("");
  const [floorList, setFloorList] = useState([]);
  const [currentFloor, setCurrentFloor] = useState("Niveau 0");
  const [annotationText, setAnnotationText] = useState("");

  const lot = lotDatas || (lotsInfos && lotsInfos.lots && lotsInfos.lots.find(x => x.name === lotParam));

  useEffect(() => {
    if (programmeName && lotsInfos) {
      setCurrent(`detail-lot/${lotParam}/plan`);
      setHeaderLevel(2);

      fetch(`${domain}/${programmeName}/detail-lot`, {
        method: "GET",
        mode: "cors",
      })
        .then((response) => response.json())
        .then((data) => setAnnotationText(data.annotationText))
        .catch(() => setAnnotationText(""));

      const currentLot = lotsInfos.lots.find((x) => x.name === lotParam);
      if (currentLot) {
        setLotDatas(currentLot);
        setDisplayPrices(lotsInfos.infos.displayPrices);

        if (currentLot.havePdf === true && currentLot.haveImages === false) {
          setCurrentDisplay("2D");
        }
        // Gestion de la source 2D
        if (currentLot.havePdf) {
          setSource2D(
            <iframe
              src={`/public/programmes/${programmeName}/lots/${lotParam}/2D/${lotParam}.pdf#view=FitV&toolbar=0&navpanes=0&scrollbar=0`}
              type="application/pdf"
              title="Plan 2D"
            />
          );
        } else {
          setSource2D(
            <img id="lot-placeholder" src={`/public/images/soon.jpg`} alt="Bientôt disponible" />
          );
        }
        loadLot(
          `/public/programmes/${programmeName}/lots/${lotParam}/3D/`,
          lotParam
        );
      }
    }
  }, [programmeName, lotParam, lotsInfos, setCurrent, setHeaderLevel, setLotDatas, loadLot]);

  useEffect(() => {
    if (lot) {
      if (lot.etage === 1) setSup("er");
      else if (lot.etage) setSup("");
      else setSup("ème");

      if (lot.sysType && lot.sysType.includes("Triplex")) {
        setFloorList(["Niveau 0", "Niveau 1", "Niveau 2"]);
      } else if (lot.sysType && lot.sysType.includes("Duplex")) {
        setFloorList(["Niveau 0", "Niveau 1"]);
      } else {
        setFloorList(["Niveau 0"]);
      }
    }
  }, [lot]);

  useEffect(() => {
    const mainNavigation = document.getElementById("mainNavigation");
    if (mainNavigation && noHeader) {
      mainNavigation.style.display = "block";
    }
  }, [noHeader]);

  const getLotDescription = (lot, lang) => {
    const isTypeNumer = Number.isFinite(lot.type);
    if (!isTypeNumer) {
      return lot.type;
    } else if (lang === "en" && isTypeNumer) {
      return lot.numPieces - 1;
    } else {
      return lot.type
    }
  };

  if (!lotsInfos || !lotsInfos.lots || !lotsInfos.infos) {
    return <div>Chargement...</div>;
  }

  return (
    <div id="detail-lot">
      <div id="detail-lot-infos">
        {lotsInfos.infos.ficheLot.displayType && (
          <div id="infosLot-type" className="infosLot">
            <p>
              {programmeDatas.languagesDatas[lang]["detail-lot"].page.type}
            </p>
            <h3>{getLotDescription(lot, lang)}</h3>
          </div>
        )}

        {lotsInfos.infos.ficheLot.displayFloor && lot.etage != null && (
          <div id="infosLot-etage" className="infosLot">
            <p>
              {programmeDatas.languagesDatas[lang]["detail-lot"].page.etage}
            </p>
            <h3>
              {typeof lot.etage === "string"
                ? lot.etage.replace(
                  "Etage",
                  programmeDatas.languagesDatas[lang].maquette.palette.etages
                )
                : lot.etage}
              <sup>{sup}</sup>
            </h3>
          </div>
        )}

        {lotsInfos.infos.ficheLot.displayTotalSurface && (
          <div id="infosLot-surface" className="infosLot">
            <p>
              {programmeDatas.languagesDatas[lang]["detail-lot"].page.surface}
            </p>
            <h3>{lot.surface}</h3>
          </div>
        )}

        {lot.pieces &&
          lot.pieces
            .filter(
              (p) =>
                lotsInfos.infos.ficheLot.surfacesInfos[p.name] &&
                lotsInfos.infos.ficheLot.surfacesInfos[p.name].display
            )
            .map((p) => (
              <div key={p.name} id="infosLot-surface" className="infosLot">
                <p>{p.name} (m²)</p>
                <h3>{p.surface}</h3>
              </div>
            ))}

        {displayPrices === true &&
          lot.prix != null &&
          lot.prix !== 0 && (
            <div id="infosLot-prix" className="infosLot">
              <p>Prix (€)</p>
              <h3>{lot.prix.toLocaleString()}</h3>
            </div>
          )}

        {lot.haveImages && lot.havePdf && (
          <div id="infosLot-switch">
            <p
              onClick={() => setCurrentDisplay("2D")}
              style={{ cursor: "pointer" }}
            >
              2D
            </p>
            <p
              onClick={() => setCurrentDisplay("3D")}
              style={{ cursor: "pointer" }}
            >
              3D
            </p>
            <div
              id="current-display"
              className={`mainBackground lot-${currentDisplay}`}
            >
              <p>{currentDisplay}</p>
            </div>
          </div>
        )}

        {currentDisplay === "3D" && floorList.length > 1 && (
          <div id="infosLot-palettes">
            <Palette
              list={[...floorList].reverse()}
              current={currentFloor}
              setCurrent={setCurrentFloor}
            />
          </div>
        )}

        {lot.havePdf && lotsInfos.infos.ficheLot.displayDownload && (
          <a
            id="infosLot-download"
            className="btnToggle"
            href={`/public/programmes/${programmeName}/lots/${lot.name}/2D/${lot.name}.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            download
          >
            <SvgIcon icon="telecharger" />
            <p>
              {
                programmeDatas.languagesDatas[lang]["detail-lot"].page
                  .telecharger
              }
            </p>
            <div className="roomGo">
              <SvgIcon icon="fleche-r" />
            </div>
          </a>
        )}
      </div>

      {currentDisplay === "2D" ? (
        <div id="detail-lot-img">{source2D}</div>
      ) : !lot.isFixe ? (
        <Player
          programme={programmeName}
          navUrl={`${lot.name}/3D/Etage${floorList.indexOf(currentFloor)}`}
          type="lots"
          numFrames={30}
          start={0}
          maxResolution={1080}
          loadHdForFrame={loadHdForFrame}
        />
      ) : (
        <img
          id="lot-img"
          src={`/public/programmes/${programmeName}/lots/${lot.name}/3D/Etage${floorList.indexOf(
            currentFloor
          )}/jpg-1080/000.jpg`}
          alt="Vue 3D du lot"
        />
      )}

      {annotationText !== "" && currentDisplay === "3D" && (
        <Annotation text={annotationText} leftoffset={200} />
      )}
    </div>
  );
};

export default DetailLot;
